import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { VideoAddModal } from './VideoAddModal';
import { useAuth } from '@/hooks/useAuth';
import { usePlan } from '@/hooks/usePlan';
import { ConfirmDialog } from './ConfirmDialog';
import { UpgradeModal } from './UpgradeModal';
import { GalleryUploadZone } from './GalleryUploadZone';
import { GalleryRowCard, type GalleryRowData } from './GalleryRowCard';
import { UploadSimple } from '@phosphor-icons/react';
import { optimizeImage, optimizeImages, formatOptimizationStats, type OptimizationResult } from '@/lib/imageOptimizer';

interface GalleryRow {
  id: string;
  left_url: string;
  right_url: string;
  title: string | null;
  sort_order: number;
  video_url?: string | null;
  video_display_mode?: string | null;
}

interface AdminProjectImagesProps {
  mode?: 'project' | 'home' | 'menu-gallery';
  projectId?: string;
  sectionSlug?: string;
  menuItemId?: string;
}

type Side = 'left' | 'right';

type DragPayload =
  | { type: 'row'; rowId: string }
  | { type: 'image'; rowId: string; side: Side; url: string };

const monoFont = "'IBM Plex Mono', monospace";
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 20 * 1024 * 1024;
const DRAG_MIME = 'application/x-mostra-gallery-dnd';

const hasImage = (url: string | null | undefined) => Boolean(url && url.trim().length > 0);
const sideKey = (side: Side) => (side === 'left' ? 'left_url' : 'right_url');

function validateFile(file: File) {
  return ACCEPTED_TYPES.includes(file.type) && file.size <= MAX_FILE_SIZE;
}

function validateFiles(files: File[]) {
  return files.filter(validateFile);
}

function readDragPayload(e: React.DragEvent): DragPayload | null {
  const raw = e.dataTransfer.getData(DRAG_MIME);
  if (!raw) return null;
  try { return JSON.parse(raw) as DragPayload; } catch { return null; }
}

function setDragPayload(e: React.DragEvent, payload: DragPayload) {
  e.dataTransfer.effectAllowed = 'move';
  e.dataTransfer.setData(DRAG_MIME, JSON.stringify(payload));
  e.dataTransfer.setData('text/plain', payload.type);
}

function getImageCount(row: GalleryRow) {
  return (hasImage(row.left_url) ? 1 : 0) + (hasImage(row.right_url) ? 1 : 0);
}

function isRowEmpty(row: GalleryRow) {
  return !hasImage(row.left_url) && !hasImage(row.right_url);
}

function getSingleImageUrl(row: GalleryRow) {
  return row.left_url || row.right_url || '';
}

function normalizeRows(rows: GalleryRow[]) {
  return rows.map((row) => {
    if (hasImage(row.left_url) && hasImage(row.right_url) && row.left_url === row.right_url) {
      return { ...row, right_url: '' };
    }
    return row;
  });
}

export function AdminProjectImages({ mode = 'project', projectId, sectionSlug = 'home', menuItemId }: AdminProjectImagesProps) {
  const { session } = useAuth();
  const { isFree, limits } = usePlan();
  const isHomeMode = mode === 'home';
  const isMenuGallery = mode === 'menu-gallery';
  const tableName = isHomeMode ? 'home_images' : isMenuGallery ? 'menu_gallery_images' : 'project_images';

  const [images, setImages] = useState<GalleryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragRowId, setDragRowId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; label: string } | null>(null);
  const [emptyRowTarget, setEmptyRowTarget] = useState<string | null>(null);
  const [insertHoverIndex, setInsertHoverIndex] = useState<number | null>(null);
  const [optStats, setOptStats] = useState<OptimizationResult[]>([]);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const dragCounterRef = useRef(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const fetchImages = useCallback(async () => {
    if (!isHomeMode && !isMenuGallery && !projectId) {
      setImages([]);
      setLoading(false);
      return;
    }
    if (isMenuGallery && !menuItemId) {
      setImages([]);
      setLoading(false);
      return;
    }

    const db = supabase as any;
    let query = db.from(tableName).select('*').order('sort_order');
    if (isMenuGallery) query = query.eq('menu_item_id', menuItemId);
    else if (!isHomeMode) query = query.eq('project_id', projectId);

    const { data } = await query;
    setImages(normalizeRows(((data || []) as unknown[]) as GalleryRow[]));
    setLoading(false);
  }, [isHomeMode, isMenuGallery, projectId, menuItemId, tableName]);

  useEffect(() => {
    setLoading(true);
    fetchImages();
  }, [fetchImages]);

  const reindexRows = useCallback(async () => {
    if (!isHomeMode && !isMenuGallery && !projectId) return;
    if (isMenuGallery && !menuItemId) return;
    const db = supabase as any;
    let query = db.from(tableName).select('id').order('sort_order');
    if (isMenuGallery) query = query.eq('menu_item_id', menuItemId);
    else if (!isHomeMode) query = query.eq('project_id', projectId);
    const { data } = await query;
    if (!data) return;
    await Promise.all(
      (data as Array<{ id: string }>).map((row, index) =>
        db.from(tableName).update({ sort_order: index }).eq('id', row.id),
      ),
    );
  }, [isHomeMode, isMenuGallery, projectId, menuItemId, tableName]);

  const persistRows = useCallback(
    async (rows: GalleryRow[]) => {
      const normalized = rows.map((row, i) => ({ ...row, sort_order: i }));
      setImages(normalized);
      await Promise.all(
        normalized.map((row) =>
          supabase
            .from(tableName as any)
            .update({
              left_url: row.left_url || '',
              right_url: row.right_url || '',
              sort_order: row.sort_order,
              title: row.title ?? null,
            })
            .eq('id', row.id),
        ),
      );
    },
    [tableName],
  );

  const uploadToStorage = async (file: File, index: number): Promise<string | null> => {
    const userId = session?.user?.id;
    if (!userId) return null;
    const ext = file.name.split('.').pop();
    const unique = crypto.randomUUID?.() ?? Math.random().toString(36).slice(2);
    const basePath = isHomeMode ? 'home' : isMenuGallery ? `menu-gallery/${menuItemId}` : `${sectionSlug}/${projectId}`;
    const path = `${userId}/${basePath}/${Date.now()}-${index}-${unique}.${ext}`;
    const { error } = await supabase.storage.from('gallery-images').upload(path, file);
    if (error) return null;
    const { data } = supabase.storage.from('gallery-images').getPublicUrl(path);
    return data.publicUrl;
  };

  const buildInsertPayload = (url: string, sortOrder: number) => {
    if (isHomeMode) {
      return { user_id: session?.user.id, left_url: url, right_url: '', sort_order: sortOrder, title: null };
    }
    if (isMenuGallery) {
      return { menu_item_id: menuItemId, left_url: url, right_url: '', sort_order: sortOrder, title: null };
    }
    return { project_id: projectId, left_url: url, right_url: '', sort_order: sortOrder, title: null };
  };

  const handleFiles = async (files: FileList | File[]) => {
    if (uploading) return;
    const valid = validateFiles(Array.from(files));
    if (!valid.length) return;

    // Check image limit for free plan (project mode only)
    if (!isHomeMode && isFree && limits.maxImagesPerGallery !== null) {
      if (images.length + valid.length > limits.maxImagesPerGallery) {
        setShowUpgrade(true);
        return;
      }
    }

    setUploading(true);
    setOptStats([]);

    const optimized = await optimizeImages(valid);
    setOptStats(optimized);

    const urls = await Promise.all(optimized.map((r, i) => uploadToStorage(r.file, i)));
    const validUrls = urls.filter((u): u is string => Boolean(u));

    if (validUrls.length) {
      await supabase.from(tableName as any).insert(
        validUrls.map((url, i) => buildInsertPayload(url, images.length + i)),
      );
      await fetchImages();
    }

    setUploading(false);
    setTimeout(() => setOptStats([]), 8000);
  };

  const uploadIntoSlot = async (rowId: string, side: Side, file: File) => {
    if (!validateFile(file)) return;
    const result = await optimizeImage(file);
    setOptStats([result]);
    setTimeout(() => setOptStats([]), 8000);
    const url = await uploadToStorage(result.file, Date.now());
    if (!url) return;
    const key = sideKey(side);
    const nextRows = images.map((row) => (row.id === rowId ? { ...row, [key]: url } : row));
    await persistRows(nextRows);
  };

  const removeRow = async (id: string) => {
    await supabase.from(tableName as any).delete().eq('id', id);
    await reindexRows();
    await fetchImages();
  };

  const removeImageFromSide = async (rowId: string, side: Side) => {
    const row = images.find((r) => r.id === rowId);
    if (!row) return;
    const key = sideKey(side);
    const nextRows = images.map((r) => (r.id === rowId ? { ...r, [key]: '' } : r));
    // If row becomes empty, remove it
    const updatedRow = nextRows.find((r) => r.id === rowId);
    if (updatedRow && isRowEmpty(updatedRow)) {
      await removeRow(rowId);
    } else {
      await persistRows(nextRows);
    }
  };

  const mergeWithNext = async (index: number) => {
    if (index >= images.length - 1) return;
    const current = images[index];
    const next = images[index + 1];
    if (getImageCount(current) !== 1 || getImageCount(next) !== 1) return;

    const mergedLeft = getSingleImageUrl(current);
    const mergedRight = getSingleImageUrl(next);

    await supabase.from(tableName as any).update({ left_url: mergedLeft, right_url: mergedRight }).eq('id', current.id);
    await supabase.from(tableName as any).delete().eq('id', next.id);
    await reindexRows();
    await fetchImages();
  };

  const splitPair = async (index: number) => {
    const row = images[index];
    if (!hasImage(row.right_url) || !hasImage(row.left_url)) return;

    const movedUrl = row.right_url;
    await supabase.from(tableName as any).update({ left_url: row.left_url, right_url: '' }).eq('id', row.id);
    await supabase.from(tableName as any).insert(buildInsertPayload(movedUrl, row.sort_order + 0.5));
    await reindexRows();
    await fetchImages();
  };

  const saveTitle = async (id: string, value: string) => {
    await supabase.from(tableName as any).update({ title: value || null }).eq('id', id);
    await fetchImages();
  };

  const saveVideoUrl = async (id: string, value: string) => {
    await supabase.from(tableName as any).update({ video_url: value || null }).eq('id', id);
    await fetchImages();
  };

  const handleAddVideo = async (data: { videoUrl: string; thumbnailUrl: string; displayMode: string }) => {
    const db = supabase as any;
    let payload: Record<string, any>;
    if (isHomeMode) {
      payload = { user_id: session?.user.id, left_url: data.thumbnailUrl || '', right_url: '', sort_order: images.length, title: null, video_url: data.videoUrl, video_display_mode: data.displayMode };
    } else if (isMenuGallery) {
      payload = { menu_item_id: menuItemId, left_url: data.thumbnailUrl || '', right_url: '', sort_order: images.length, title: null, video_url: data.videoUrl, video_display_mode: data.displayMode };
    } else {
      payload = { project_id: projectId, left_url: data.thumbnailUrl || '', right_url: '', sort_order: images.length, title: null, video_url: data.videoUrl, video_display_mode: data.displayMode };
    }
    await db.from(tableName).insert(payload);
    await fetchImages();
  };

  const swapSides = async (rowId: string) => {
    const nextRows = images.map((row) =>
      row.id === rowId ? { ...row, left_url: row.right_url, right_url: row.left_url } : row,
    );
    await persistRows(nextRows);
  };

  // Row drag
  const handleRowDragStart = (e: React.DragEvent, rowId: string) => {
    setDragRowId(rowId);
    setDragPayload(e, { type: 'row', rowId });
  };

  const handleRowDrop = async (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    const payload = readDragPayload(e);
    if (!payload || payload.type !== 'row') return;

    const fromIndex = images.findIndex((row) => row.id === payload.rowId);
    if (fromIndex < 0 || fromIndex === targetIndex) {
      setDragRowId(null);
      return;
    }

    const reordered = [...images];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    await persistRows(reordered);
    setDragRowId(null);
  };

  const maybePromptEmptyRow = (row: GalleryRow | undefined) => {
    if (!row) return;
    if (isRowEmpty(row)) setEmptyRowTarget(row.id);
  };

  const handleImageDragStart = (e: React.DragEvent, rowId: string, side: Side, url: string) => {
    setDragPayload(e, { type: 'image', rowId, side, url });
  };

  const handleImageDropOnSlot = async (e: React.DragEvent, targetRowId: string, targetSide: Side) => {
    e.preventDefault();
    const payload = readDragPayload(e);
    if (!payload || payload.type !== 'image') return;

    const sourceIndex = images.findIndex((row) => row.id === payload.rowId);
    const targetIndex = images.findIndex((row) => row.id === targetRowId);
    if (sourceIndex < 0 || targetIndex < 0) return;

    const sourceKey = sideKey(payload.side);
    const targetKey = sideKey(targetSide);
    const nextRows = [...images];
    const sourceRow = { ...nextRows[sourceIndex] };
    const targetRow = { ...nextRows[targetIndex] };
    const sourceUrl = sourceRow[sourceKey];
    if (!hasImage(sourceUrl)) return;

    if (payload.rowId === targetRowId) {
      if (payload.side === targetSide) return;
      const targetUrl = targetRow[targetKey];
      sourceRow[sourceKey] = targetUrl;
      sourceRow[targetKey] = sourceUrl;
      nextRows[sourceIndex] = sourceRow;
      await persistRows(nextRows);
      return;
    }

    const targetUrl = targetRow[targetKey];
    if (hasImage(targetUrl)) {
      sourceRow[sourceKey] = targetUrl;
      targetRow[targetKey] = sourceUrl;
    } else {
      sourceRow[sourceKey] = '';
      targetRow[targetKey] = sourceUrl;
    }
    nextRows[sourceIndex] = sourceRow;
    nextRows[targetIndex] = targetRow;
    await persistRows(nextRows);
    maybePromptEmptyRow(nextRows[sourceIndex]);
  };

  const handleInsertDragOver = (e: React.DragEvent, index: number) => {
    const payload = readDragPayload(e);
    if (!payload || payload.type !== 'image') return;
    e.preventDefault();
    setInsertHoverIndex(index);
  };

  const handleInsertDrop = async (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setInsertHoverIndex(null);
    const payload = readDragPayload(e);
    if (!payload || payload.type !== 'image') return;

    const sourceIndex = images.findIndex((row) => row.id === payload.rowId);
    if (sourceIndex < 0) return;
    const sourceKey = sideKey(payload.side);
    const sourceRow = { ...images[sourceIndex] };
    const movedUrl = sourceRow[sourceKey];
    if (!hasImage(movedUrl)) return;

    sourceRow[sourceKey] = '';
    await supabase.from(tableName as any).update({ left_url: sourceRow.left_url || '', right_url: sourceRow.right_url || '' }).eq('id', sourceRow.id);

    const rowsWithoutMoved = [...images];
    rowsWithoutMoved[sourceIndex] = sourceRow;
    const before = rowsWithoutMoved[index - 1];
    const after = rowsWithoutMoved[index];
    const beforeSort = before?.sort_order ?? -1;
    const afterSort = after?.sort_order ?? beforeSort + 2;
    const insertSort = (beforeSort + afterSort) / 2;

    await supabase.from(tableName as any).insert(buildInsertPayload(movedUrl, insertSort));
    await reindexRows();
    await fetchImages();
    maybePromptEmptyRow(sourceRow);
  };

  const removeEmptyRow = async () => {
    if (!emptyRowTarget) return;
    await supabase.from(tableName as any).delete().eq('id', emptyRowTarget);
    setEmptyRowTarget(null);
    await reindexRows();
    await fetchImages();
  };

  const renderInsertZone = (insertIndex: number) => (
    <div
      key={`insert-${insertIndex}`}
      onDragOver={(e) => handleInsertDragOver(e, insertIndex)}
      onDrop={(e) => handleInsertDrop(e, insertIndex)}
      onDragLeave={() => setInsertHoverIndex((prev) => (prev === insertIndex ? null : prev))}
      className="h-3 transition-all"
      style={{
        backgroundColor: insertHoverIndex === insertIndex ? 'rgba(247,246,242,0.05)' : 'transparent',
        borderTop: insertHoverIndex === insertIndex ? '1px solid rgba(247,246,242,0.3)' : '1px solid transparent',
      }}
    />
  );

  const handleContainerDragEnter = useCallback((e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    dragCounterRef.current++;
    setIsDraggingFiles(true);
  }, []);

  const handleContainerDragOver = useCallback((e: React.DragEvent) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
  }, []);

  const handleContainerDragLeave = useCallback((e: React.DragEvent) => {
    dragCounterRef.current--;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDraggingFiles(false);
    }
  }, []);

  const handleContainerDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    dragCounterRef.current = 0;
    setIsDraggingFiles(false);
    if (e.dataTransfer.files?.length) {
      const imageFiles = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
      if (imageFiles.length > 0) {
        handleFiles(imageFiles);
      }
    }
  }, [handleFiles]);

  if (loading) {
    return <p className="text-xs" style={{ fontFamily: monoFont, color: 'rgba(247,246,242,0.4)' }}>Carregando imagens...</p>;
  }

  return (
    <div
      ref={containerRef}
      onDragEnter={handleContainerDragEnter}
      onDragOver={handleContainerDragOver}
      onDragLeave={handleContainerDragLeave}
      onDrop={handleContainerDrop}
      style={{ position: 'relative', minHeight: '100%' }}
    >
      {isDraggingFiles && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            zIndex: 50,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            border: '1px dashed rgba(247,246,242,0.4)',
            backgroundColor: 'rgba(10,10,10,0.85)',
            pointerEvents: 'none',
          }}
        >
          <UploadSimple size={40} weight="thin" color="rgba(247,246,242,0.5)" />
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 12,
              fontWeight: 300,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: 'rgba(247,246,242,0.5)',
            }}
          >
            SOLTAR PARA ENVIAR
          </span>
        </div>
      )}

      <UpgradeModal
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        title="Limite de imagens atingido"
        description={`No plano grátis você pode ter até ${limits.maxImagesPerGallery} imagens por galeria. Faça upgrade para o Pro e tenha imagens ilimitadas.`}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title="Remover imagem"
        description={deleteTarget?.label || 'Deseja remover esta imagem?'}
        confirmLabel="Remover"
        cancelLabel="Cancelar"
        destructive
        onConfirm={() => {
          if (deleteTarget) removeRow(deleteTarget.id);
        }}
      />

      <ConfirmDialog
        open={emptyRowTarget !== null}
        onOpenChange={(open) => { if (!open) setEmptyRowTarget(null); }}
        title="Linha vazia"
        description="Esta linha ficou vazia. Deseja removê-la?"
        confirmLabel="Remover linha"
        cancelLabel="Manter linha"
        destructive
        onConfirm={removeEmptyRow}
      />

      <VideoAddModal open={showVideoModal} onClose={() => setShowVideoModal(false)} onAdd={handleAddVideo} />

      <div className="flex gap-4 items-stretch mb-4">
        <div className="flex-1">
          <GalleryUploadZone uploading={uploading} onFiles={handleFiles} compact={images.length > 0} />
        </div>
        <button
          onClick={() => setShowVideoModal(true)}
          className="flex flex-col items-center justify-center gap-2 px-6 shrink-0"
          style={{
            border: '1px dashed rgba(247,246,242,0.2)',
            fontFamily: monoFont,
            fontSize: 12,
            fontWeight: 300,
            color: 'rgba(247,246,242,0.4)',
            textTransform: 'uppercase',
            cursor: 'pointer',
            backgroundColor: 'transparent',
            transition: 'border-color 0.15s ease',
            letterSpacing: '0.05em',
            minHeight: images.length > 0 ? 48 : 100,
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(247,246,242,0.4)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(247,246,242,0.2)'; }}
        >
          <span style={{ fontSize: images.length > 0 ? 14 : 20 }}>▶</span>
          + ADICIONAR VÍDEO
        </button>
      </div>

      {/* Optimization stats */}
      {optStats.length > 0 && optStats.some((s) => s.savedPercent > 0) && (
        <div className="p-3 flex flex-col gap-1 mb-4" style={{ border: '0.5px solid hsl(120 40% 50% / 0.2)', backgroundColor: 'hsl(120 40% 50% / 0.05)' }}>
          <div className="flex items-center gap-2 mb-1">
            <UploadSimple size={12} weight="thin" style={{ color: 'hsl(120 40% 40%)' }} />
            <span className="text-[10px] uppercase tracking-wider" style={{ fontFamily: monoFont, color: 'hsl(120 40% 40%)' }}>
              Otimização concluída
            </span>
          </div>
          {optStats.filter((s) => s.savedPercent > 0).map((stat, i) => (
            <span key={i} className="text-[10px]" style={{ fontFamily: monoFont, opacity: 0.7 }}>
              {formatOptimizationStats(stat)}
            </span>
          ))}
        </div>
      )}

      <div className="flex flex-col">
        {renderInsertZone(0)}

        {images.map((img, i) => {
          const imageCount = getImageCount(img);
          const single = imageCount === 1;
          const hasBothSides = imageCount === 2;
          const canMerge = single && i < images.length - 1 && getImageCount(images[i + 1]) === 1;

          const rowData: GalleryRowData = {
            id: img.id,
            leftUrl: img.left_url,
            rightUrl: img.right_url,
            title: img.title,
            sortOrder: img.sort_order,
            videoUrl: img.video_url,
            videoDisplayMode: img.video_display_mode,
          };

          return (
            <Fragment key={img.id}>
              <GalleryRowCard
                row={rowData}
                index={i}
                mode="gallery"
                isDragging={dragRowId === img.id}
                onDragStart={(e) => handleRowDragStart(e, img.id)}
                onDragEnd={() => setDragRowId(null)}
                onDragOver={(e) => {
                  if (!e.dataTransfer.types.includes(DRAG_MIME)) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                }}
                onDrop={(e) => handleRowDrop(e, i)}
                renderLeftSlot={
                  <ImageSlot
                    rowId={img.id}
                    side="left"
                    url={img.left_url}
                    onDragStart={handleImageDragStart}
                    onDropImage={handleImageDropOnSlot}
                    onUpload={uploadIntoSlot}
                  />
                }
                renderRightSlot={
                  <ImageSlot
                    rowId={img.id}
                    side="right"
                    url={img.right_url}
                    onDragStart={handleImageDragStart}
                    onDropImage={handleImageDropOnSlot}
                    onUpload={uploadIntoSlot}
                  />
                }
                onSwapSides={() => swapSides(img.id)}
                onSaveTitle={(val) => saveTitle(img.id, val)}
                onSaveVideoUrl={(val) => saveVideoUrl(img.id, val)}
                canMerge={canMerge}
                canSplit={hasBothSides}
                onMerge={() => mergeWithNext(i)}
                onSplit={() => splitPair(i)}
                onDeleteRow={() => setDeleteTarget({ id: img.id, label: 'Deseja remover esta linha da galeria?' })}
                onDeleteLeft={() => removeImageFromSide(img.id, 'left')}
                onDeleteRight={() => removeImageFromSide(img.id, 'right')}
              />
              {renderInsertZone(i + 1)}
            </Fragment>
          );
        })}
      </div>

      {images.length === 0 && (
        <p className="text-xs text-center py-8" style={{ fontFamily: monoFont, color: 'rgba(247,246,242,0.4)' }}>
          Nenhuma imagem ainda. Arraste fotos para a área acima.
        </p>
      )}
    </div>
  );
}

function ImageSlot({
  rowId,
  side,
  url,
  onDragStart,
  onDropImage,
  onUpload,
}: {
  rowId: string;
  side: Side;
  url: string;
  onDragStart: (e: React.DragEvent, rowId: string, side: Side, url: string) => void;
  onDropImage: (e: React.DragEvent, targetRowId: string, targetSide: Side) => void;
  onUpload: (rowId: string, side: Side, file: File) => void;
}) {
  const filled = hasImage(url);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = (file: File | undefined) => {
    if (!file) return;
    onUpload(rowId, side, file);
  };

  return (
    <div
      onClick={() => { if (!filled) inputRef.current?.click(); }}
      onDragOver={(e) => {
        const isInternalDrag = e.dataTransfer.types.includes(DRAG_MIME);
        const isFileDrag = e.dataTransfer.types.includes('Files');
        if (!isInternalDrag && !isFileDrag) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
      }}
      onDrop={(e) => {
        if (e.dataTransfer.files?.length) {
          e.preventDefault();
          handleFile(e.dataTransfer.files[0]);
          return;
        }
        onDropImage(e, rowId, side);
      }}
      className={`overflow-hidden flex items-center justify-center ${filled ? '' : 'cursor-pointer'}`}
      style={{
        width: 64,
        height: 64,
        backgroundColor: filled ? 'transparent' : 'rgba(247,246,242,0.04)',
      }}
      title={filled ? `Arrastar imagem (${side})` : `Clique ou arraste`}
    >
      {filled ? (
        <img
          src={url}
          alt=""
          draggable
          onDragStart={(e) => onDragStart(e, rowId, side, url)}
          className="w-full h-full object-cover cursor-grab"
        />
      ) : (
        <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, textTransform: 'uppercase', color: 'rgba(247,246,242,0.25)', textAlign: 'center', lineHeight: 1.3, padding: 4 }}>
          clique ou arraste
        </span>
      )}
      <input
        ref={inputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.webp"
        className="hidden"
        onChange={(e) => {
          handleFile(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </div>
  );
}
